import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Activity, ChevronDown, ChevronRight, Clock, Zap, Coins,
  CheckCircle2, XCircle, Loader2,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const statusBadge = {
  completed: { bg: "bg-emerald-50 text-emerald-600 border-emerald-200", label: "Completed" },
  running: { bg: "bg-blue-50 text-blue-600 border-blue-200", label: "Running" },
  failed: { bg: "bg-red-50 text-red-600 border-red-200", label: "Failed" },
};

const agentStatusIcon = {
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  in_progress: <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />,
  pending: <div className="w-3.5 h-3.5 rounded-full border-2 border-foreground/15" />,
  failed: <XCircle className="w-3.5 h-3.5 text-red-500" />,
};

export const Observability = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pipeline-runs`, { withCredentials: true });
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load pipeline runs:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const totalRuns = runs.length;
  const completedRuns = runs?.filter(r => r.status === "completed").length;
  const totalTokens = runs.reduce((s, r) => s + (r.totalTokens || 0), 0);
  const totalCost = runs.reduce((s, r) => s + (r.totalCost || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="observability-loading">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/45" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="observability-page">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 bg-white border-b border-border/40">
        <h2 className="font-heading font-bold text-lg text-foreground mb-4">Observability</h2>
        <div className="flex gap-6">
          {[
            { val: totalRuns, label: "Total Runs", icon: Activity, color: "text-[#014d7d]" },
            { val: completedRuns, label: "Completed", icon: CheckCircle2, color: "text-emerald-600" },
            { val: `${(totalTokens / 1000).toFixed(1)}K`, label: "Total Tokens", icon: Zap, color: "text-amber-500" },
            { val: `$${totalCost.toFixed(2)}`, label: "Total Cost", icon: Coins, color: "text-purple-600" },
          ]?.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-foreground/[0.04] flex items-center justify-center">
                <s.icon className={`w-4.5 h-4.5 ${s.color}`} />
              </div>
              <div>
                <p className={`text-xl font-heading font-bold ${s.color}`}>{s.val}</p>
                <p className="text-[10px] text-foreground/40 font-semibold uppercase tracking-wider">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Runs list */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3" data-testid="runs-list">
        {runs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-foreground/45">
            <Activity className="w-10 h-10 mb-3" />
            <p className="text-sm font-semibold">No pipeline runs yet</p>
            <p className="text-xs mt-1">Go to Pipelines and run an orchestration</p>
          </div>
        ) : (
          runs?.map((run) => {
            const badge = statusBadge[run.status] || statusBadge.completed;
            const isExpanded = expanded[run.id];
            return (
              <div key={run.id} className="bg-white border border-border/60 rounded-2xl overflow-hidden" data-testid={`run-${run.id}`}>
                <button onClick={() => toggle(run.id)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-foreground/[0.01]" data-testid={`toggle-run-${run.id}`}>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-foreground/45 shrink-0" /> : <ChevronRight className="w-4 h-4 text-foreground/45 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{run.pipeline}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-foreground/40">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {run.duration}</span>
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {((run.totalTokens || 0) / 1000).toFixed(1)}K tokens</span>
                      <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> ${(run.totalCost || 0).toFixed(2)}</span>
                      <span>{(run.agents || []).length} agents</span>
                      <span>{run.assets || 0} assets</span>
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${badge.bg}`}>{badge.label}</span>
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-border/30 pt-3" data-testid={`run-detail-${run.id}`}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-foreground/50 uppercase tracking-wider text-[10px]">
                            <th className="text-left py-2 pr-3">Agent</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-left py-2 pr-3">Duration</th>
                            <th className="text-right py-2 pr-3">Tokens</th>
                            <th className="text-right py-2 pr-3">Cost</th>
                            <th className="text-right py-2">Outputs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(run.agents || [])?.map((agent, i) => (
                            <tr key={i} className="border-t border-border/20">
                              <td className="py-2.5 pr-3 flex items-center gap-2">
                                {agentStatusIcon[agent.status] || agentStatusIcon.pending}
                                <span className="font-semibold text-foreground">{agent.name}</span>
                              </td>
                              <td className="py-2.5 pr-3">
                                <span className={`capitalize ${agent.status === "completed" ? "text-emerald-600" : agent.status === "failed" ? "text-red-600" : "text-foreground/40"}`}>
                                  {agent.status}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3 text-foreground/50">{agent.time || "—"}</td>
                              <td className="py-2.5 pr-3 text-right text-foreground/50">{agent.tokens ? `${(agent.tokens / 1000).toFixed(1)}K` : "—"}</td>
                              <td className="py-2.5 pr-3 text-right text-foreground/50">{agent.cost ? `$${agent.cost.toFixed(2)}` : "—"}</td>
                              <td className="py-2.5 text-right text-foreground/50">{agent.outputs || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/20 text-[10px] text-foreground/40">
                      <span>Run ID: {run.id?.slice(0, 8)}</span>
                      <span>Started: {run.started ? new Date(run.started).toLocaleString() : "—"}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
